import React, { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/context/LanguageContext";

export default function Contact({ contact }) {
  const { t, ui } = useT();

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    preferred_date: "",
    service_type: "Casas",
    frequency: "Una sola vez",
    address: "",
    city: "",
    bedrooms: "",
    bathrooms: "",
    sqft: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error(ui("form.toast_missing"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(ui("form.toast_success"));
      setForm({
        name: "",
        phone: "",
        email: "",
        preferred_date: "",
        service_type: "Casas",
        frequency: "Una sola vez",
        address: "",
        city: "",
        bedrooms: "",
        bathrooms: "",
        sqft: "",
        notes: "",
      });
    } catch (err) {
      toast.error(ui("form.toast_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      className="relative bg-[color:var(--gcs-ivory)] py-24 md:py-40"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5"
          >
            <p className="overline text-[color:var(--gcs-gold)] mb-4">{ui("contact.chapter")}</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-[color:var(--gcs-emerald)]">
              {ui("contact.h1_a")}<em className="text-[color:var(--gcs-gold)]">{ui("contact.h1_em")}</em>{ui("contact.h1_b")}
            </h2>
            <p className="mt-6 text-base md:text-lg text-[color:var(--gcs-emerald)]/75 leading-relaxed max-w-md">
              {ui("contact.lead")}
            </p>

            <div className="mt-10 space-y-6">
              <div>
                <p className="overline text-[color:var(--gcs-gold)] mb-2">{ui("contact.phone")}</p>
                <a
                  href={`tel:${contact?.phone?.replace(/[^0-9+]/g, "") || ""}`}
                  className="font-serif text-2xl md:text-3xl text-[color:var(--gcs-emerald)] gcs-underline"
                  data-testid="contact-phone"
                >
                  {contact?.phone}
                </a>
              </div>
              <div>
                <p className="overline text-[color:var(--gcs-gold)] mb-2">{ui("contact.email")}</p>
                <a
                  href={`mailto:${contact?.email}`}
                  className="font-serif text-xl md:text-2xl text-[color:var(--gcs-emerald)] gcs-underline break-all"
                  data-testid="contact-email"
                >
                  {contact?.email}
                </a>
              </div>
              <div>
                <p className="overline text-[color:var(--gcs-gold)] mb-2">{ui("contact.area")}</p>
                <span className="font-serif text-xl text-[color:var(--gcs-emerald)]">{t(contact?.service_area)}</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`https://wa.me/${contact?.whatsapp}`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full btn-emerald px-5 py-2.5 text-xs font-semibold inline-flex items-center gap-2"
                  data-testid="contact-whatsapp"
                >
                  {ui("cta.whatsapp")}
                </a>
                <a
                  href={`tel:${contact?.phone?.replace(/[^0-9+]/g, "") || ""}`}
                  className="rounded-full btn-outline-emerald px-5 py-2.5 text-xs font-semibold"
                  data-testid="contact-call-btn"
                >
                  {ui("cta.call")}
                </a>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-7 bg-[color:var(--gcs-ivory-2)] p-8 md:p-12 rounded-sm border border-[color:var(--gcs-line)]"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              <input className="input-editorial" placeholder={ui("form.name")} value={form.name} onChange={onChange("name")} data-testid="form-name" />
              <input className="input-editorial" placeholder={ui("form.phone")} value={form.phone} onChange={onChange("phone")} data-testid="form-phone" />
              <input className="input-editorial" placeholder={ui("form.email")} value={form.email} onChange={onChange("email")} data-testid="form-email" />
              <input className="input-editorial" type="date" placeholder={ui("form.date")} value={form.preferred_date} onChange={onChange("preferred_date")} data-testid="form-date" />
              <select className="input-editorial" value={form.service_type} onChange={onChange("service_type")} data-testid="form-service">
                <option>Casas</option>
                <option>Limpieza Profunda</option>
                <option>Apartamentos</option>
                <option>Oficinas</option>
                <option>Move-in / Move-out</option>
                <option>Post-Construcción</option>
              </select>
              <select className="input-editorial" value={form.frequency} onChange={onChange("frequency")} data-testid="form-frequency">
                <option>{ui("form.freq_once")}</option>
                <option>{ui("form.freq_weekly")}</option>
                <option>{ui("form.freq_biweekly")}</option>
                <option>{ui("form.freq_monthly")}</option>
              </select>
              <input className="input-editorial" placeholder={ui("form.address")} value={form.address} onChange={onChange("address")} data-testid="form-address" />
              <input className="input-editorial" placeholder={ui("form.city")} value={form.city} onChange={onChange("city")} data-testid="form-city" />
              <input className="input-editorial" placeholder={ui("form.bedrooms")} value={form.bedrooms} onChange={onChange("bedrooms")} data-testid="form-bedrooms" />
              <input className="input-editorial" placeholder={ui("form.bathrooms")} value={form.bathrooms} onChange={onChange("bathrooms")} data-testid="form-bathrooms" />
              <input className="input-editorial md:col-span-2" placeholder={ui("form.sqft")} value={form.sqft} onChange={onChange("sqft")} data-testid="form-sqft" />
              <textarea className="input-editorial md:col-span-2 min-h-[100px] resize-none" placeholder={ui("form.notes")} value={form.notes} onChange={onChange("notes")} data-testid="form-notes" />
            </div>
            <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <p className="text-xs text-[color:var(--gcs-emerald)]/60 max-w-sm">{ui("form.disclaimer")}</p>
              <button
                type="submit"
                disabled={loading}
                className="rounded-full btn-emerald px-8 py-3.5 text-sm font-semibold disabled:opacity-60"
                data-testid="form-submit"
              >
                {loading ? ui("state.sending") : ui("form.submit")}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
